import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Download, RefreshCw, Search, Hourglass, LayoutGrid, List } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import apiClient from '@/services/apiClient';
import { AgeRangeEditor } from '@/components/storageAgeing/AgeRangeEditor';
import { AgeingBucketStrip } from '@/components/storageAgeing/AgeingBucketStrip';
import { AgeingSummaryTable } from '@/components/storageAgeing/AgeingSummaryTable';
import { AgeingItemsTable } from '@/components/storageAgeing/AgeingItemsTable';
import {
  STORAGE_TABS, loadBreaks, saveBreaks, selectionParams, startExcelDownload,
} from '@/components/storageAgeing/ageingUtils';

const Pill = ({ active, onClick, children, testid }) => (
  <button type="button" onClick={onClick} data-testid={testid}
    className={`px-4 py-2 rounded-full text-sm font-semibold transition-colors duration-200 ${active ? 'bg-stone-900 text-white shadow' : 'text-stone-600 hover:bg-stone-200'}`}>
    {children}
  </button>
);

export default function StorageAgeingReport({ defaultTab = 'greige' }) {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const tab = STORAGE_TABS.some((t) => t.id === params.get('tab')) ? params.get('tab') : defaultTab;
  const [breaks, setBreaks] = useState(loadBreaks);
  const [search, setSearch] = useState('');
  const [applied, setApplied] = useState('');
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [view, setView] = useState('summary');
  const [drill, setDrill] = useState(null);
  const [items, setItems] = useState(null);
  const [itemsLoading, setItemsLoading] = useState(false);

  const loadReport = useCallback(async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams({ storage: tab, breaks: breaks.join(',') });
      if (applied) p.append('search', applied);
      const res = await apiClient.get(`/storage-ageing/report?${p.toString()}`);
      setReport(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load ageing report');
    } finally {
      setLoading(false);
    }
  }, [tab, breaks, applied]);

  const loadItems = useCallback(async () => {
    setItemsLoading(true);
    try {
      const p = selectionParams({ storage: tab, breaks, search: applied, drill });
      const res = await apiClient.get(`/storage-ageing/items?${p.toString()}`);
      setItems(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load items');
    } finally {
      setItemsLoading(false);
    }
  }, [tab, breaks, applied, drill]);

  useEffect(() => { setReport(null); loadReport(); }, [loadReport]);
  useEffect(() => { if (view === 'items') loadItems(); }, [view, loadItems]);
  useEffect(() => { setDrill(null); setView('summary'); setItems(null); }, [tab]);

  const changeTab = (id) => setParams({ tab: id }, { replace: true });
  const applyBreaks = (b) => { saveBreaks(b); setBreaks(b); toast.success('Age ranges updated'); };
  const openDrill = (group, bucket) => { setDrill({ group, bucket }); setView('items'); };
  const refresh = () => { loadReport(); if (view === 'items') loadItems(); };
  const exportExcel = () => {
    startExcelDownload(selectionParams({ storage: tab, breaks, search: applied, drill: view === 'items' ? drill : null }));
    toast.success('Excel download started');
  };

  const hint = STORAGE_TABS.find((t) => t.id === tab)?.hint;

  return (
    <div className="min-h-screen bg-[#F7F5F0] text-stone-900" data-testid="storage-ageing-page">
      <div className="max-w-[1500px] mx-auto px-4 md:px-8 py-6 space-y-5">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="flex items-start gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate(-1)} className="text-stone-600 hover:text-stone-900 mt-1" data-testid="ageing-back-btn">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-2xl md:text-3xl font-bold tracking-tight flex items-center gap-2">
                <Hourglass className="w-7 h-7 text-amber-600" /> Stock Ageing Report
              </h1>
              <p className="text-sm text-stone-600 mt-1">Age = days since the stock was first put away into the storage. {hint}.</p>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <AgeRangeEditor breaks={breaks} onApply={applyBreaks} />
            <Button variant="outline" onClick={refresh} className="border-stone-300 bg-white" data-testid="ageing-refresh-btn">
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} /> Refresh
            </Button>
            <Button onClick={exportExcel} className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="ageing-export-btn">
              <Download className="w-4 h-4 mr-2" /> Download Excel
            </Button>
          </div>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="inline-flex gap-1 rounded-full bg-stone-200/70 p-1" data-testid="ageing-storage-tabs">
            {STORAGE_TABS.map((t) => <Pill key={t.id} active={tab === t.id} onClick={() => changeTab(t.id)} testid={`ageing-tab-${t.id}`}>{t.label}</Pill>)}
          </div>
          <form className="relative w-full sm:w-80" onSubmit={(e) => { e.preventDefault(); setApplied(search.trim()); }}>
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} onBlur={() => setApplied(search.trim())}
              placeholder={tab === 'greige' ? 'Design, colour, bundle…' : 'Design, shade, SKU, bin…'} className="pl-9 bg-white border-stone-300" data-testid="ageing-search-input" />
          </form>
        </div>

        {!report ? (
          <div className="py-24 text-center text-stone-500" data-testid="ageing-loading">Loading ageing…</div>
        ) : (
          <>
            <AgeingBucketStrip report={report} onPick={(b) => openDrill(null, b)} />
            <div className="inline-flex gap-1 rounded-full bg-stone-200/70 p-1">
              <Pill active={view === 'summary'} onClick={() => setView('summary')} testid="ageing-view-summary"><LayoutGrid className="w-4 h-4 inline mr-1.5 -mt-0.5" />Summary</Pill>
              <Pill active={view === 'items'} onClick={() => setView('items')} testid="ageing-view-items"><List className="w-4 h-4 inline mr-1.5 -mt-0.5" />Item list</Pill>
            </div>
            {view === 'summary'
              ? <AgeingSummaryTable key={`${tab}-${breaks.join()}-${applied}`} report={report} onDrill={openDrill} />
              : <AgeingItemsTable storage={tab} data={items} loading={itemsLoading} drill={drill} onClearDrill={() => setDrill(null)} />}
          </>
        )}
      </div>
    </div>
  );
}
